import { useState, type ReactElement } from "react";
import { Link, useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { ValueField } from "./contract-fields.js";
import { LoadingPage } from "./loading-page.js";
import { tenantApi, tenantUrls } from "./urls.js";

/** 入力画面を開くのに要るもの（<c>GET /api/tenants/{契約}/ip-restriction</c>）。 */
export interface TenantIpRestriction {
    readonly tenantId: number;
    /** システム名。 */
    readonly title: string;
    readonly allowIpAddresses: string | null;
}

const title = "接続を許可する IP アドレス";

/**
 * 最後の確認はブラウザの確認ダイアログで行う（接続できなくなりうる操作。docs/01 §7）。
 * 許可していない場所からはプリザンターに接続できなくなるため、送る前にもう 1 段止める。
 */
export const ipRestrictionDialog = (systemTitle: string): string =>
    `「${systemTitle}」の接続を許可する IP アドレスを変更します。許可していない場所からはプリザンターに接続できなくなります。よろしいですか？`;

function IpRestrictionForm({ value }: { readonly value: TenantIpRestriction }): ReactElement {
    usePageMeta(title);
    const form = useApiForm(tenantApi.ipRestriction(value.tenantId), undefined, ipRestrictionDialog(value.title));
    const [values, setValues] = useState<Readonly<Record<string, string>>>({ AllowIpAddresses: value.allowIpAddresses ?? "" });

    return (
        <div className="page max-w-2xl">
            <p className="mb-4">
                <Link to={tenantUrls.detail(value.tenantId)} className="btn-secondary btn-sm">契約情報へ戻る</Link>
            </p>
            {/* 題は枠の中の先頭、戻るは枠の外の上（docs/01 §6） */}
            <div className="panel">
                <h1 className="page-title">{title}</h1>
                <p className="page-lead">{value.title}</p>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />

                    <ValueField
                        label="接続を許可する IP アドレス" name="AllowIpAddresses" rows={5} autoFocus
                        wrapperClassName="mb-7" inputClassName="font-mono"
                        placeholder={"192.168.0.1\n203.0.113.0/24"}
                        help="1 行に 1 件で入力してください。CIDR 表記（/0〜/32）も使えます。空にすると制限しません。"
                        value={values["AllowIpAddresses"] ?? ""} onChange={(name, text) => { setValues({ ...values, [name]: text }); }}
                        error={form.fieldError("AllowIpAddresses")}
                    />

                    <div className="flex gap-3">
                        <Link to={tenantUrls.detail(value.tenantId)} className="btn-secondary">キャンセル</Link>
                        <button type="submit" className="btn-primary grow" disabled={form.busy}>変更する</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/**
 * 接続を許可する IP アドレス（<c>/Tenant/IpRestriction/{契約}</c>）。契約情報の「接続を許可する IP アドレス」から開く。
 * 開けるかは API が決める（開けなければ 404 →「見つからない」）。終われば契約情報で知らせる（API の notice）。
 */
export function IpRestrictionPage(): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<TenantIpRestriction>(tenantApi.ipRestriction(tenantId));

    return data.state === "ready"
        ? <IpRestrictionForm value={data.value} />
        : <LoadingPage title={title} data={data} width="max-w-2xl" />;
}
